import './AppsFilterSwitch.scss';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../extra/sharedMethods';
import { AnalyticsEvent } from '../../scripts/constants/analytics-event';
import { useFlag, useUserSafe } from '../../scripts/hooks';
import { useAppName } from '../../scripts/hooks/apps';
import { isSquareImg } from '../../scripts/utils';
import { LinkFavicon } from '../controls/LinkFavicon/LinkFavicon';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { UITooltip } from '../controls/ui/UIToolTip/UIToolTip';
import { ConnectedApps } from '../jit-qa/QATextInputView';
import { Tabs, TabsList, TabsTrigger } from '../shadcn/lib/components/tabs';

type IAppsFilterTabNames = 'aiOnly' | 'apps' | 'web';

interface IAppsFilterSwitchProps {
  isBaseLLM: boolean;
  searchFilters: ConnectedApps[];
  setIsBaseLLM: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchFilters: React.Dispatch<React.SetStateAction<ConnectedApps[]>>;
  sortedInstantConnectedApps: ConnectedApps[];
}

const getTabValue = (searchFilters: ConnectedApps[], isBaseLLM: boolean) => {
  if (isBaseLLM) {
    return 'aiOnly';
  }

  if (searchFilters.some((app) => app.appName === 'web')) {
    return 'web';
  }

  return 'apps';
};

// eslint-disable-next-line max-lines-per-function
export const AppsFilterSwitch: React.FC<IAppsFilterSwitchProps> = ({
  isBaseLLM,
  searchFilters,
  setIsBaseLLM,
  setSearchFilters,
  sortedInstantConnectedApps,
}: IAppsFilterSwitchProps) => {
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);
  const [tab, setTab] = useState<IAppsFilterTabNames>(
    getTabValue(searchFilters, isBaseLLM)
  );

  const [isSquareLogo, setIsSquareLogo] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const chevronRef = useRef<SVGElement>(null);
  const isAiOnlyEnabled = useFlag('aiOnlyEnabled');
  const { getAppName } = useAppName();
  const {
    orgByOrg: {
      preferences: { logo },
    },
  } = useUserSafe();

  useEffect(() => {
    setTab(getTabValue(searchFilters, isBaseLLM));
  }, [searchFilters, isBaseLLM]);

  const handleAppsButton = useCallback(() => {
    if (searchFilters.some((app) => app.appName === 'web') || isBaseLLM) {
      setSearchFilters(sortedInstantConnectedApps);
      setIsBaseLLM(false);
    }

    setIsBaseLLM(false);

    trackEvent(AnalyticsEvent.QAAppsFilterButtonClicked, {
      searchFilters,
    });
  }, [
    isBaseLLM,
    searchFilters,
    setIsBaseLLM,
    setSearchFilters,
    sortedInstantConnectedApps,
  ]);

  const toggleIsMultiSelectOpen = useCallback(() => {
    setIsMultiSelectOpen(!isMultiSelectOpen);
  }, [isMultiSelectOpen, setIsMultiSelectOpen]);

  const setSearchFiltersToWeb = useCallback(() => {
    setSearchFilters([{ appName: 'web', displayName: 'Web' }]);
    setIsBaseLLM(false);
    trackEvent(AnalyticsEvent.QAWebFilterButtonClicked);
  }, [setIsBaseLLM, setSearchFilters]);

  const setSearchFiltersToAiOnly = useCallback(() => {
    setIsBaseLLM(true);
    setSearchFilters([]);
    trackEvent(AnalyticsEvent.QAWebFilterButtonClicked);
  }, [setIsBaseLLM, setSearchFilters]);

  const handleCheckChange = useCallback(
    (app: ConnectedApps) => {
      const checked = searchFilters.some((obj) =>
        obj.id ? obj.id === app.id : obj.appName === app.appName
      );

      if (checked) {
        setSearchFilters((prev) =>
          prev.filter((item) =>
            item.id ? item.id !== app.id : item.appName !== app.appName
          )
        );
      } else {
        setSearchFilters((prev) => [...prev, app]);
      }
    },

    [searchFilters, setSearchFilters]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        chevronRef.current &&
        !chevronRef.current.contains(event.target as Node)
      ) {
        setIsMultiSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTabChange = useCallback((value: string) => {
    setTab(value as IAppsFilterTabNames);
  }, []);

  const displayName = useCallback(
    (app: ConnectedApps) => {
      if (app.connectionName && app.connectionName !== '') {
        return app.connectionName;
      }

      const appName = getAppName(app.id);
      if (appName) {
        return appName;
      }

      return app.displayName.split(' ').slice(0, 2).join(' ');
    },
    [getAppName]
  );

  useEffect(() => {
    let isMounted = true;

    const checkIfSquareLogo = async () => {
      if (isMounted && logo) {
        const res = await isSquareImg(logo);
        setIsSquareLogo(res);
      } else {
        setIsSquareLogo(false);
      }
    };

    checkIfSquareLogo();

    return () => {
      isMounted = false;
    };
  }, [logo]);

  return (
    <Tabs onValueChange={handleTabChange} value={tab}>
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger
          className="relative"
          onMouseDown={handleAppsButton}
          value="apps"
        >
          {isMultiSelectOpen && (
            <>
              <div className="overlay" />
              <div className="appSelect text-black" ref={selectRef}>
                <div className="border-b max-h-[300px] overflow-y-auto">
                  {sortedInstantConnectedApps.map((app) => {
                    const selected = searchFilters.some((obj) =>
                      obj.id ? obj.id === app.id : obj.appName === app.appName
                    );

                    return (
                      <li
                        className={classNames('gap-3', {
                          appSelected: selected,
                        })}
                        key={app.id}
                        onClick={() => {
                          handleCheckChange(app);
                        }}
                      >
                        <div className="iconName">
                          {app.appName === 'website' ? (
                            <LinkFavicon
                              link={`http://${app.workspaceName!}`}
                              size={20}
                            />
                          ) : (
                            <UIIcon name={app.appName} type="apps" />
                          )}
                          <span>{displayName(app)}</span>
                        </div>
                        <UIIcon
                          className={`${selected ? '' : 'invisible'}`}
                          name="check"
                        />
                      </li>
                    );
                  })}
                </div>
                <div className="controlButtons">
                  <div
                    className={classNames(
                      {
                        'text-cloud-20':
                          searchFilters.length ===
                          sortedInstantConnectedApps.length,
                      },
                      'font-medium'
                    )}
                    onClick={() => {
                      setSearchFilters(sortedInstantConnectedApps);
                    }}
                  >
                    Select all
                  </div>
                  <div className="text-cloud-15">|</div>
                  <div
                    className={classNames(
                      {
                        'text-cloud-20': searchFilters.length === 0,
                      },
                      'font-medium'
                    )}
                    onClick={() => {
                      setSearchFilters([]);
                    }}
                  >
                    Clear all
                  </div>
                </div>
              </div>
            </>
          )}

          {isSquareLogo ? (
            <UITooltip title="Search Apps">
              <div className="rounded flex">
                <img alt="favicon" height={16} src={logo} width={16} />
              </div>
            </UITooltip>
          ) : (
            <UIIcon name="app" tooltip="Search Apps" />
          )}
          <div
            className={`ml-1 sm:block hidden text-cloud-40 ${
              tab === 'apps' ? '!text-black' : ''
            }`}
          >
            Apps
          </div>
          {sortedInstantConnectedApps.length > 0 && (
            <UITooltip title="Select Apps to search">
              <UIIcon
                className="selectChevron hover:pt-1"
                name="arrow-down"
                onClick={toggleIsMultiSelectOpen}
                ref={chevronRef}
              />
            </UITooltip>
          )}
        </TabsTrigger>
        <TabsTrigger onMouseDown={setSearchFiltersToWeb} value="web">
          <UIIcon name="web" size={16} tooltip="Search Web" type="apps" />
          <div
            className={`ml-1 sm:block hidden text-cloud-40 ${
              tab === 'web' ? '!text-black' : ''
            }`}
          >
            Web
          </div>
        </TabsTrigger>
        {isAiOnlyEnabled && (
          <TabsTrigger onMouseDown={setSearchFiltersToAiOnly} value="aiOnly">
            <UIIcon name="ai-sparkle" tooltip="Ask AI without search" />
            <div
              className={`ml-1 sm:block hidden text-cloud-40 ${
                tab === 'aiOnly' ? '!text-black' : ''
              }`}
            >
              AI Only
            </div>
          </TabsTrigger>
        )}
      </TabsList>
    </Tabs>
  );
};
